// Reading and writing domain.userVisibleSubscriptions, which magellan-api accepts in
// two shapes and stores in one:
//
//   legacy   ['newsletter', 'promotions']
//   stored   [{ key: 'newsletter', label: 'Weekly newsletter' }, { key: 'promotions' }]
//
// The API migrated the column to the object shape and narrows every write to it, so
// this UI writes objects too. It still READS the legacy shape, because a domain loaded
// from an API that predates that migration (or a rollback) hands back plain names, and
// a settings tab that showed every switch off would look like data loss.
//
// A label is optional and stays that way: absent means "derive it", and the derivation
// lives in the API (formatSubscriptionLabel). defaultSubscriptionLabel below is a copy
// of that transformation, used only to fill the placeholder a merchant types over —
// never written, so a drift between the two cannot reach stored data.

export const MARKETING_SUBSCRIPTION = 'marketing';

// Mirror of magellan-api's formatSubscriptionLabel. Placeholder only.
export const defaultSubscriptionLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const readEntry = (entry) => {
  if (typeof entry === 'string') {
    return { key: entry.trim(), label: '' };
  }

  if (!entry || typeof entry !== 'object' || Array.isArray(entry) || typeof entry.key !== 'string') {
    return null;
  }

  return { key: entry.key.trim(), label: typeof entry.label === 'string' ? entry.label : '' };
};

/**
 * The curated list as the UI works with it: `[{ key, label }]`, label '' when none is
 * set. Junk entries are dropped, matching what the API's reader does with them.
 */
export const readCuratedSubscriptions = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  const byKey = new Map();

  for (const entry of value) {
    const read = readEntry(entry);
    if (!read || !read.key) {
      continue;
    }

    const existing = byKey.get(read.key);
    if (existing) {
      if (read.label && !existing.label) {
        existing.label = read.label;
      }
      continue;
    }

    byKey.set(read.key, read);
  }

  return [...byKey.values()];
};

/** True when the domain has curated a list, as opposed to leaving the default. */
export const isCurated = (value) => readCuratedSubscriptions(value).length > 0;

/**
 * What the subscription manager actually renders for this domain, which is what the tab
 * has to show to be honest about the effect of a change.
 *
 * An uncurated domain (`[]`, the column default, or a missing value) exposes `marketing`
 * alone — the API resolves that through a fallback rather than from the column, so the
 * tab shows it ticked even though the column is empty. Ticking anything then
 * materialises this list, which is why writeCuratedSubscriptions is always given the
 * effective list rather than the raw column.
 *
 * (A NULL column also picks up the API's `subscriptions`-registry fallback, which this
 * cannot reproduce without that registry. Post-migration the column is `[]` rather than
 * NULL for every domain that never curated, so the case is unreachable in practice.)
 */
export const effectiveSubscriptions = (value) => {
  const curated = readCuratedSubscriptions(value);

  return curated.length > 0 ? curated : [{ key: MARKETING_SUBSCRIPTION, label: '' }];
};

/**
 * The write shape: `{ key }`, or `{ key, label }` when a label is set. A blank label is
 * sent as an explicit `label: ''`, which is how the API is told to clear a stored one —
 * omitting the field would make it inherit instead.
 *
 * The label itself is passed through untouched (not trimmed): the `TextInput` in
 * SettingsTabSubscriptionManagement is controlled off this written value, so every
 * keystroke round-trips back through here. Trimming on each write would eat a trailing
 * space the moment it is typed, making any multi-word label unreachable. Only the
 * whitespace-only case is normalised to `''`, which is the explicit-clear signal.
 */
export const writeCuratedSubscriptions = (subscriptions) =>
  subscriptions.map(({ key, label }) => ({ key, label: label && label.trim() ? label : '' }));
